import { configureStore } from '@reduxjs/toolkit'
import { setupListeners } from '@reduxjs/toolkit/query'
import { useSelector } from 'react-redux'
import type { TypedUseSelectorHook } from 'react-redux'
import { combineReducers } from 'redux'
import {
  FLUSH,
  PAUSE,
  PERSIST,
  PURGE,
  REGISTER,
  REHYDRATE,
  persistReducer,
  persistStore,
} from 'redux-persist'
import storage from 'redux-persist/lib/storage'

import { allInOneSlice } from './allInOne'
import { apiService } from './apiService'
import { appSlice } from './appSlice'
import digitalTryInReducer from './digitalTryInSlice'
import { expireTransform } from './expireTransform'
import { fullLayeringSlice } from './fullLayering'
import { fullMonolithicSlice } from './fullMonoLithic'
import { gingivaSlice } from './gingiva'
import { matisseLiteSlice } from './matisseLite'
import { microLayeringSlice } from './microLayering'
import { pfmCrownSlice } from './pfmCrown'
import { projectsSlice } from './project'
import sessionSlice from './sessionSlice'
import { shadeGuideSlice } from './shadeGuide'
import { stainingStudioSlice } from './stainingStudio'

// Define types for better type safety
const rootReducer = combineReducers({
  [apiService.reducerPath]: apiService.reducer,
  app: appSlice.reducer,
  allInOne: allInOneSlice.reducer,
  session: sessionSlice,
  digitalTryIn: digitalTryInReducer,
  shadeGuide: shadeGuideSlice.reducer,
  stainingStudio: stainingStudioSlice.reducer,
  fullLayering: fullLayeringSlice.reducer,
  microLayering: microLayeringSlice.reducer,
  fullMonolithic: fullMonolithicSlice.reducer,
  pfmCrown: pfmCrownSlice.reducer,
  gingiva: gingivaSlice.reducer,
  project: projectsSlice.reducer,
  matisseLite: matisseLiteSlice.reducer,
})

export type RootState = ReturnType<typeof rootReducer>
export type AppDispatch = typeof store.dispatch

const persistConfig = {
  key: 'root',
  version: 1,
  storage,
  blacklist: ['register', 'session', 'digitalTryIn'],
  transforms: [expireTransform],
  writeFailHandler: (error: Error) => {
    console.error('Redux Persist write failed:', error)
  },
}

const persistedReducer = persistReducer<RootState>(persistConfig, rootReducer)

// Separate middleware configuration for better organization
const getMiddleware = (getDefaultMiddleware: any) => {
  const middleware = getDefaultMiddleware({
    serializableCheck: {
      ignoredActions: [FLUSH, REHYDRATE, PAUSE, PERSIST, PURGE, REGISTER],
      ignoredPaths: ['register'],
    },
    immutableCheck: { warnAfter: 128 },
    thunk: true,
  })

  return middleware.concat(apiService.middleware)
}

export const store = configureStore({
  reducer: persistedReducer,
  devTools: process.env.NODE_ENV === 'development',
  middleware: getMiddleware,
})

// Add development-only store augmentation
if (process.env.NODE_ENV === 'development') {
  // @ts-ignore - window augmentation for debugging
  window.__store__ = store
}

export const persistor = persistStore(store)

// Setup RTK Query listeners
setupListeners(store.dispatch)

// Export hooks for TypeScript users
export const useAppDispatch = () => store.dispatch
export const useAppSelector: TypedUseSelectorHook<RootState> = useSelector
